/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from '../../../app/store';
import { snackNotifications } from '../notification/SnackBarUtils';

export interface UiState {
  sidebarOpen: boolean;
  mobileSidebarOpen: boolean; // whether open in mobile mode where it popsover the content
  apiVersion: [string, string] | undefined;
}

const initialState: UiState = {
  sidebarOpen: (localStorage.getItem('sideDrawer.open') || '') === 'true',
  mobileSidebarOpen: false,
  apiVersion: undefined,
};

export const uiSlice = createSlice({
  name: 'ui',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    openSideBar: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
      localStorage.setItem('sideDrawer.open', state.sidebarOpen.toString());
    },
    toggleSideBar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
      localStorage.setItem('sideDrawer.open', state.sidebarOpen.toString());
    },
    mobileSideBarOpen: (state, action: PayloadAction<boolean>) => {
      state.mobileSidebarOpen = action.payload;
    },
    setApiVersion: (state, action: PayloadAction<string>) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [ver, _, sha] = action.payload.split(' ');
      if (state.apiVersion) {
        // It has changed!!
        snackNotifications.infoTop(
          <span>
            New version of the app is available. Please{' '}
            <a href={window.location.href} onClick={() => window.location.reload()} style={{ color: 'darkblue' }}>
              refresh
            </a>{' '}
            the page
          </span>,
          {
            persist: true,
            type: 'NEW_APP_VERSION',
          }
        );
        // eslint-disable-next-line no-restricted-globals
        // location.reload();
      }
      state.apiVersion = [ver, sha] as [string, string];
    },
  },
});

export const { openSideBar, mobileSideBarOpen, setApiVersion, toggleSideBar } = uiSlice.actions;

export const selectIsSidebarOpen = (state: RootState): boolean => state.ui.sidebarOpen;
export const selectIsMobileSidebarOpen = (state: RootState): boolean => state.ui.mobileSidebarOpen;
export const selectApiVersion = (state: RootState) => state.ui.apiVersion;

export default uiSlice.reducer;
